import type { ReactNode } from 'react';
import { Badge, Button, Group } from '@mantine/core';
import type { StatusTone } from '../tokens/statusColorMap';
import { statusToneToMantineColor } from '../tokens/statusColorMap';

export interface FilterChipItem<T extends string> {
  id: T;
  label: string;
  count?: number;
  icon?: ReactNode;
}

export interface FilterChipGroupProps<T extends string> {
  items: FilterChipItem<T>[];
  activeId: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
  className?: string;
}

function resolveChipTone(id: string): StatusTone | undefined {
  if (id === 'problem' || id === 'errors') {
    return 'danger';
  }
  if (id === 'unmatched') {
    return 'warning';
  }
  if (id === 'conflict') {
    return 'info';
  }
  if (id === 'review' || id === 'action') {
    return 'warning';
  }
  if (id === 'ready' || id === 'done' || id === 'posted') {
    return 'success';
  }
  if (id === 'all') {
    return 'neutral';
  }
  return undefined;
}

export function FilterChipGroup<T extends string>({
  items,
  activeId,
  onChange,
  ariaLabel,
  className,
}: FilterChipGroupProps<T>) {
  return (
    <Group gap={6} role="tablist" aria-label={ariaLabel} wrap="wrap" className={className}>
      {items.map((item) => {
        const active = item.id === activeId;
        const tone = resolveChipTone(item.id);
        const color = tone ? statusToneToMantineColor[tone] : 'gray';

        return (
          <Button
            key={item.id}
            role="tab"
            aria-selected={active}
            variant={active ? 'filled' : 'light'}
            color={active ? 'blue' : color}
            size="compact-sm"
            leftSection={item.icon}
            rightSection={
              item.count !== undefined ? (
                <Badge
                  size="xs"
                  variant={active ? 'white' : 'light'}
                  color={active ? 'blue' : color}
                  circle={item.count < 10}
                  style={{ minWidth: item.count >= 10 ? 20 : undefined }}
                >
                  {item.count}
                </Badge>
              ) : undefined
            }
            onClick={() => onChange(item.id)}
            styles={{
              root: {
                flexShrink: 0,
              },
              section: {
                marginInlineEnd: item.count !== undefined ? 4 : undefined,
              },
            }}
          >
            {item.label}
          </Button>
        );
      })}
    </Group>
  );
}
